import type { LucideIcon } from 'lucide-react';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/shared/components/ui';
import { cn } from '@/shared/utils';

interface PageTitleProps {
  /** Icon to display next to the title (matches prototype design) */
  icon?: LucideIcon;
  /** Optional test id for the icon */
  iconTestId?: string;
  /** Optional icon key for tests/telemetry */
  iconKey?: string;
  /** Breadcrumb items to display above the title */
  breadcrumb?: ReactNode;
  /** Main page title */
  title: string;
  /** Optional subtitle/description */
  subtitle?: string;
  /** Primary action button config */
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
    disabled?: boolean;
  };
  /** Additional actions (rendered as-is) */
  actions?: ReactNode;
  className?: string;
}

/**
 * Page title component matching the prototype design.
 * Includes icon, breadcrumb, title, and action buttons.
 */
export function PageTitle({
  icon: Icon,
  iconTestId,
  iconKey,
  breadcrumb,
  title,
  subtitle,
  primaryAction,
  actions,
  className,
}: PageTitleProps) {
  return (
    <div
      className={cn(
        'flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4',
        className
      )}
    >
      <div className="flex items-center gap-4">
        {Icon && (
          <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center text-primary shrink-0">
            <Icon className="size-6" data-testid={iconTestId} data-icon-key={iconKey} />
          </div>
        )}
        <div>
          {breadcrumb && (
            <nav className="flex text-sm text-muted-foreground mb-1">{breadcrumb}</nav>
          )}
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
      </div>

      {(primaryAction || actions) && (
        <div className="flex gap-3">
          {actions}
          {primaryAction && (
            <Button
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              className="flex items-center gap-2 shadow-sm"
            >
              {primaryAction.icon || <Plus className="size-4" />}
              {primaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

interface BreadcrumbItemProps {
  children: ReactNode;
  isLast?: boolean;
}

/**
 * Simple breadcrumb item for use in PageTitle
 */
export function SimpleBreadcrumbItem({ children, isLast }: BreadcrumbItemProps) {
  return (
    <>
      <span className={isLast ? 'text-foreground font-medium' : ''}>{children}</span>
      {!isLast && <span className="mx-2">/</span>}
    </>
  );
}
